import { useEffect, useState } from 'react'
import { api } from '@carrent/api'
import { DeltaStat, FlagList, Icon, ImageCompare, SplitView } from '@carrent/ui'

import TripDamageCompare from './TripDamageCompare'

/**
 * Сравнение акта приёмки и акта сдачи.
 *
 * Экран решения, а не просмотра. Диспетчер приходит сюда ответить
 * на один вопрос — принимать поездку или нет, — поэтому сначала
 * дельты и флаги, потом доказательства, и только потом всё
 * остальное. Обратный порядок (сначала фото, дельты где-то внизу)
 * заставляет искать глазами то, что уже посчитано.
 *
 * ── Два представления, потому что задачи две ──────────────────────
 *
 * «По парам» — сравнить один и тот же ракурс до и после. Здесь
 * работает наложение со слайдером: снимки лежат в одной коробке.
 *
 * «Актами» — просмотреть акт целиком, включая ракурсы, которых при
 * сдаче не снимают. Здесь важна синхронная прокрутка, а наложение
 * бессмысленно: сравнивать левый борт не с чем.
 *
 * ── Флаги показываются заметно, но без паники ─────────────────────
 *
 * Флаг — повод посмотреть внимательнее, а не приговор водителю.
 * Поэтому у каждого написано, что именно сработало, и красный цвет
 * достаётся только критичному.
 */

function formatDuration(minutes) {
  if (minutes === null || minutes === undefined) return '—'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h ? `${h} ч ${m} мин` : `${m} мин`
}

function formatKm(value) {
  return value === null || value === undefined ? '—' : `${value.toLocaleString('ru')} км`
}

function formatTime(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' })
}

/** Колонка одного акта для режима «актами». */
function ActColumn({ photos, emptyText }) {
  if (!photos.length) return <p className="compare__empty">{emptyText}</p>

  return photos.map((photo) => (
    <figure key={`${photo.category}-${photo.id}`} className="compare__act-shot">
      <img src={photo.url} alt={photo.label || photo.category} loading="lazy" />
      <figcaption>
        <span>{photo.label || photo.category}</span>
        {photo.taken_at && (
          <time dateTime={photo.taken_at}>{formatTime(photo.taken_at)}</time>
        )}
      </figcaption>
    </figure>
  ))
}

export default function TripComparison({ trip, onChanged }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [view, setView] = useState('pairs')

  useEffect(() => {
    let alive = true
    setData(null)
    setError(null)
    api.get(`/trips/${trip.id}/comparison`)
      .then(({ data: body }) => { if (alive) setData(body.data) })
      .catch(() => { if (alive) setError('Не удалось загрузить сравнение актов') })
    return () => { alive = false }
  }, [trip.id, onChanged])

  if (error) return <div className="alert alert--warning">{error}</div>

  if (!data) {
    return (
      <div className="compare__loading" role="status">
        <span className="spinner" />
        <span className="text-muted">Считаем расхождения…</span>
      </div>
    )
  }

  const { odometer, fuel, pairs, start_only: startOnly, flags, damage_delta: damageDelta } = data

  // Снимки приёмки для режима «актами»: пары дают ракурс приёмки,
  // start_only — то, что при сдаче не снимают вовсе.
  const startShots = [
    ...pairs.map((p) => p.start_photo && { ...p.start_photo, label: p.label, category: p.category }),
    ...startOnly.map((s) => s.photo && { ...s.photo, label: s.label, category: s.category }),
  ].filter(Boolean)
  const endShots = pairs
    .map((p) => p.end_photo && { ...p.end_photo, label: p.label, category: p.category })
    .filter(Boolean)

  return (
    <section className="compare">
      {data.partial && (
        <div className="alert alert--info">
          Поездка ещё не завершена — показаны данные приёмки.
        </div>
      )}

      <div className="compare__deltas">
        <DeltaStat
          label="Одометр"
          value={formatKm(odometer.delta)}
          hint={`${formatKm(odometer.start)} → ${formatKm(odometer.end)}`}
          tone={odometer.delta !== null && odometer.delta < 0 ? 'danger' : null}
        />
        <DeltaStat
          label="Длительность"
          value={formatDuration(data.duration_minutes)}
          hint={data.distance_per_hour ? `${data.distance_per_hour} км/ч в среднем` : null}
        />
        <DeltaStat
          label="Заправки"
          value={fuel.refuel_count ? `${fuel.liters} л` : 'нет'}
          hint={fuel.refuel_count
            ? `${fuel.refuel_count} шт · ${fuel.amount?.toLocaleString('ru')} ₽`
            : 'Уровень в баке не фиксируется'}
        />
      </div>

      <FlagList
        flags={flags}
        label="Замечания по поездке"
        cleanText="Автоматических замечаний по поездке нет."
      />

      <div className="compare__head">
        <p className="section-title">Снимки приёмки и сдачи</p>
        <div className="compare__views" role="tablist" aria-label="Представление">
          <button type="button" role="tab" aria-selected={view === 'pairs'}
            className={`compare__view${view === 'pairs' ? ' is-active' : ''}`}
            onClick={() => setView('pairs')}>
            <Icon name="overlay" size={13} />
            По парам
          </button>
          <button type="button" role="tab" aria-selected={view === 'acts'}
            className={`compare__view${view === 'acts' ? ' is-active' : ''}`}
            onClick={() => setView('acts')}>
            <Icon name="columns" size={13} />
            Актами
          </button>
        </div>
      </div>

      {view === 'pairs' ? (
        <div className="compare__pairs">
          {pairs.map((pair) => (
            <ImageCompare
              key={pair.category}
              title={pair.label}
              before={pair.start_photo && {
                url: pair.start_photo.url,
                takenAt: pair.start_photo.taken_at,
                takenAtLabel: formatTime(pair.start_photo.taken_at),
              }}
              after={pair.end_photo && {
                url: pair.end_photo.url,
                takenAt: pair.end_photo.taken_at,
                takenAtLabel: formatTime(pair.end_photo.taken_at),
              }}
            />
          ))}

          {startOnly.some((s) => s.photo) && (
            <div className="compare__only">
              <p className="compare__only-title">Снято только при выезде</p>
              <p className="compare__only-note">
                Эти ракурсы при сдаче не требуются — сравнивать их не с чем.
              </p>
              <div className="compare__only-grid">
                {startOnly.filter((s) => s.photo).map((s) => (
                  <figure key={s.category} className="compare__act-shot">
                    <img src={s.photo.url} alt={s.label} loading="lazy" />
                    <figcaption><span>{s.label}</span></figcaption>
                  </figure>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <SplitView
          label="Акты приёмки и сдачи"
          leftLabel="Акт приёмки"
          rightLabel="Акт сдачи"
          leftMeta={`${formatKm(odometer.start)} · снимков: ${startShots.length}`}
          rightMeta={data.partial
            ? 'Поездка не завершена'
            : `${formatKm(odometer.end)} · снимков: ${endShots.length}`}
          left={<ActColumn photos={startShots} emptyText="Снимков приёмки нет" />}
          right={<ActColumn photos={endShots} emptyText="Снимков сдачи пока нет" />}
        />
      )}

      {/* Вердикт по повреждениям — часть сравнения, а не отдельный
          раздел ниже: это тот же вопрос «что изменилось за поездку»,
          только на схеме кузова вместо снимков. */}
      <p className="section-title">Повреждения: акт приёмки и акт сдачи</p>
      <TripDamageCompare trip={trip} delta={damageDelta} />
    </section>
  )
}
